// ============================================================
// ANSICHTSSACHE-AGENT – Der Spaltenkopf als Sortierknopf
// ============================================================
// Wer eine Liste anders sortiert haben will, klickt auf die
// Spalte, nach der sortiert werden soll — die Beschriftung IST
// der Schalter, wie in jeder Tabellenkalkulation. Ein eigener
// Waehler „Sortierung“ im Filterkopf waere ein zweiter Ort fuer
// dieselbe Frage, und er stuende weit weg von dem, was er
// sortiert.
//
//   <TableHeader>
//     <SortableHeaderRow sort={view.sort} defaultColumn={DEFAULT_SORT}>
//       <SortableHead column="nummer" {...SORT_COLUMNS.nummer}>NR.</SortableHead>
//       <TableHead>TEAM</TableHead>          nicht jede Spalte sortiert
//       <SortableHead column="netto" {...SORT_COLUMNS.netto} align="right">NETTO</SortableHead>
//       …
//
// DIE SORTIERUNG STEHT IN DER ADRESSE, wie jeder Filter:
// `sortierung` (die Spalte) und `richtung` (`auf`/`ab`). Beide
// stehen in `SHAPE_KEYS` (`lib/saved-views.ts`) und fahren damit
// in gespeicherten Ansichten mit. Was der Vorgabe entspricht,
// faellt aus der Adresse heraus — die Liste ohne Zutun hat eine
// leere Adresse. Gelesen und sortiert wird mit `lib/sorting.ts`
// auf dem Server, VOR dem Sichtfenster: sonst sortierte der Klick
// nur die 200 gezeigten Zeilen und nicht die Liste.
//
// DIE VORGABE IST IMMER EINE SICHTBARE SPALTE. Auch ohne Klick
// steht ein Pfeil im Kopf und sagt, wie die Liste geordnet ist —
// und ein Klick auf diese Spalte fuehrt jederzeit zurueck.
//
// EINE NEUE SPALTE FAENGT DORT AN, WO MAN SUCHT: Texte bei A,
// Zahlen und Daten beim groessten bzw. neuesten (`first`). Ein
// zweiter Klick auf dieselbe Spalte dreht die Richtung.
//
// DER PFEIL SPRINGT SOFORT. Die Zeilen kommen erst mit der
// neuen Seite vom Server; der Kopf wartet darauf nicht
// (`useOptimistic` im Uebergang von `useAnsicht`). Ein Knopf, der
// erst nach der Antwort etwas zeigt, wird dreimal gedrueckt.
//
// DIE TEXTKANTE BLEIBT, wo sie war: der Knopf hat keinen
// Innenabstand, und der Pfeil steht auf der Seite, die NICHT an
// der Spaltenkante liegt — bei linksbuendigen Spalten hinter,
// bei rechtsbuendigen vor der Beschriftung. Er belegt seinen
// Platz auch unsichtbar, damit beim Ueberfahren nichts springt.
// Bei mittigen Spalten haelt links ein gleich breiter Platzhalter
// dagegen — sonst stuende die Beschriftung neben der Mitte.
//
// `align-top` AM KNOPF: ein `inline-flex` sitzt auf der
// Grundlinie seines ERSTEN Kindes. Das ist links die Schrift,
// rechts aber der Pfeil und mittig der leere Platzhalter — die
// Beschriftung stand dort 1 bzw. 2 Pixel neben ihren Nachbarn
// (nachgemessen am 29.09.2026). Oben angeschlagen fuellt der
// Knopf genau die Zeilenhoehe, wie der Text daneben.
// ============================================================

"use client";

import { createContext, useContext, useOptimistic } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { TableHead, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { SORT_PARAMS, type SortDirection, type SortParams, type SortState } from "@/lib/sorting";
import { useAnsicht } from "./filterkopf";

type SortContextValue = {
  sort: SortState;
  choose: (column: string, first: SortDirection) => void;
  isPending: boolean;
};

const SortContext = createContext<SortContextValue | null>(null);

/**
 * Die Kopfzeile einer sortierbaren Tabelle.
 *
 * Sie haelt die Sortierung EINMAL fuer alle Koepfe: klickt man
 * „Kunde“, muss der Pfeil an „Nr.“ im selben Augenblick
 * verschwinden, in dem er an „Kunde“ erscheint.
 */
export function SortableHeaderRow({
  sort,
  defaultColumn,
  params = SORT_PARAMS,
  className,
  children,
}: {
  /** Die Sortierung, wie die Seite sie aus der Adresse gelesen hat. */
  sort: SortState;
  /** Die Spalte, nach der ohne Angabe sortiert wird. */
  defaultColumn: string;
  /** Eigene Adressschluessel — nur, wo `richtung` schon ein Filter ist (siehe `lib/sorting.ts`). */
  params?: SortParams;
  className?: string;
  children: React.ReactNode;
}) {
  const { setzen, isPending } = useAnsicht();
  const [shown, show] = useOptimistic(sort);

  function choose(column: string, first: SortDirection) {
    const direction: SortDirection =
      column === shown.column ? (shown.direction === "auf" ? "ab" : "auf") : first;
    setzen(
      {
        [params.column]: column === defaultColumn ? null : column,
        [params.direction]: direction === first ? null : direction,
      },
      () => show({ column, direction }),
    );
  }

  return (
    <SortContext.Provider value={{ sort: shown, choose, isPending }}>
      <TableRow className={className}>{children}</TableRow>
    </SortContext.Provider>
  );
}

/** Ein Spaltenkopf, der nach seiner Spalte sortiert. */
export function SortableHead({
  column,
  label,
  first,
  align = "left",
  className,
  children,
}: {
  /** Der Schluessel in der Adresse (`?sortierung=kunde`). */
  column: string;
  /** Wie die Spalte im Satz heisst — „Nach Kunde sortieren“. */
  label: string;
  /** Die Richtung beim ersten Klick: `auf` fuer Text, `ab` fuer Zahl und Datum. */
  first: SortDirection;
  /** Rechtsbuendige Spalte (Betraege): der Pfeil steht dann links. Mittig: Platzhalter links, Pfeil rechts. */
  align?: "left" | "right" | "center";
  className?: string;
  children: React.ReactNode;
}) {
  const context = useContext(SortContext);
  if (!context) throw new Error("SortableHead steht nur in einer SortableHeaderRow.");

  const active = context.sort.column === column;
  const direction = active ? context.sort.direction : null;
  const Arrow = direction === "auf" ? ArrowUp : direction === "ab" ? ArrowDown : ArrowUpDown;
  const right = align === "right";

  const arrow = (
    <Arrow
      aria-hidden
      className={cn(
        "duration-fast h-3 w-3 shrink-0 transition-opacity",
        active
          ? "opacity-100"
          : "opacity-0 group-hover/sort:opacity-60 group-focus-visible/sort:opacity-60",
      )}
    />
  );

  return (
    <TableHead
      className={cn(right && "text-right", align === "center" && "text-center", className)}
      aria-sort={direction === "auf" ? "ascending" : direction === "ab" ? "descending" : undefined}
    >
      <button
        type="button"
        onClick={() => context.choose(column, first)}
        aria-busy={active && context.isPending ? true : undefined}
        title={
          active
            ? `Nach ${label} sortiert, ${direction === "auf" ? "aufsteigend" : "absteigend"} — umkehren`
            : `Nach ${label} sortieren`
        }
        className={cn(
          "group/sort text-overline duration-fast focus-visible:ring-accent/40 inline-flex items-center gap-1 rounded-sm align-top font-medium whitespace-nowrap transition-colors focus-visible:ring-2 focus-visible:outline-none",
          active ? "text-text-primary" : "text-text-tertiary hover:text-text-secondary",
        )}
      >
        {right && arrow}
        {align === "center" && <span aria-hidden className="w-3 shrink-0" />}
        <span>{children}</span>
        {!right && arrow}
      </button>
    </TableHead>
  );
}
